import { useEffect, useRef, useState } from 'react';
import { campaigns } from '@/mocks/campaigns';
import CampaignCard from './CampaignCard';
import CampaignModal from './CampaignModal';

export default function CampaignSection() {
  const [visible, setVisible] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const selectedCampaign = campaigns.find((c) => c.id === selectedId) ?? null;

  return (
    <section id="kampanyalar" className="relative py-24 md:py-32 bg-[#0a0a0f] overflow-hidden">
      {/* Glow */}
      <div className="absolute top-1/3 right-0 w-96 h-96 bg-[#d4af37]/8 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/3 left-0 w-80 h-80 bg-[#00d9ff]/6 rounded-full blur-[100px] pointer-events-none" />

      <div ref={ref} className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#d4af37] text-xs font-bold uppercase tracking-[4px] mb-4">
            Kampanyalar
          </span>
          <h2 className="text-3xl md:text-5xl font-black text-white leading-tight mb-4">
            Premium Güzellik{' '}
            <span style={{ background: 'linear-gradient(135deg, #d4af37, #f4d03f)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Kampanyaları
            </span>
          </h2>
          <p className="text-white/50 text-base max-w-xl mx-auto">
            Her kampanya, dönüşüm için optimize edilmiş bir satış hunisidir
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {campaigns.slice(0, 4).map((campaign, i) => (
            <div
              key={campaign.id}
              className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <CampaignCard campaign={campaign} onDetail={setSelectedId} />
            </div>
          ))}

          {/* VIP - full width */}
          {campaigns[4] && (
            <div
              className={`sm:col-span-2 lg:col-span-3 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              <div
                className="group relative rounded-2xl overflow-hidden border border-[#d4af37]/30 hover:border-[#d4af37]/70 transition-all duration-400 cursor-pointer hover:-translate-y-1"
                style={{ background: 'rgba(26, 26, 46, 0.8)', backdropFilter: 'blur(20px)' }}
                onClick={() => setSelectedId(campaigns[4].id)}
              >
                <div className="flex flex-col md:flex-row">
                  <div className="relative md:w-1/2 h-52 md:h-auto overflow-hidden">
                    <img
                      src={campaigns[4].image}
                      alt={campaigns[4].title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0a0a0f] hidden md:block" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] to-transparent md:hidden" />
                    <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full text-xs font-black bg-[#d4af37] text-black">
                      VIP ÖZEL
                    </div>
                  </div>
                  <div className="md:w-1/2 p-6 md:p-10 flex flex-col justify-center">
                    <div className="flex items-center gap-2 mb-3">
                      <i className="ri-vip-crown-2-line text-[#d4af37] text-xl"></i>
                      <span className="text-[#d4af37] text-xs font-bold uppercase tracking-widest">VIP Paket</span>
                    </div>
                    <h3 className="text-white font-black text-2xl md:text-3xl mb-3">{campaigns[4].title}</h3>
                    <p className="text-white/50 text-sm leading-relaxed mb-6">{campaigns[4].desc}</p>
                    <div className="flex items-center gap-4 mb-6">
                      <span className="text-white/30 text-base line-through">{campaigns[4].oldPrice}</span>
                      <span
                        className="text-3xl font-black"
                        style={{ background: 'linear-gradient(135deg, #d4af37, #f4d03f)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}
                      >
                        {campaigns[4].newPrice}
                      </span>
                    </div>
                    <button
                      onClick={() => setSelectedId(campaigns[4].id)}
                      className="w-full md:w-auto px-8 py-3.5 rounded-xl bg-gradient-to-r from-[#d4af37] to-[#f4d03f] text-black font-bold text-sm flex items-center justify-center gap-2 hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-vip-crown-2-line"></i>
                      VIP Paketi İncele
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {selectedCampaign && (
        <CampaignModal campaign={selectedCampaign} onClose={() => setSelectedId(null)} />
      )}
    </section>
  );
}
